import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import type { Role } from '@reflex/shared';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Button } from '@reflex/ui/components/ui/button';
import { Switch } from '@reflex/ui/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';

interface MemberRolesPopoverProps {
  /**
   * All roles available for assignment (typically the org's roles, filtered to
   * `scope === 'org'` for org membership and `scope === 'team'` for team
   * membership).
   */
  availableRoles: Role[];
  /**
   * Roles currently assigned to the member.
   */
  assignedRoles: Role[];
  /**
   * Stable identifier for the member, used to namespace test ids.
   */
  memberKey: string;
  onToggle: (roleId: string, next: boolean) => void;
  pending?: boolean;
  /**
   * When true, switches are read-only — the popover still renders so the user
   * can inspect their current roles without `roles:manage`.
   */
  readOnly?: boolean;
}

/**
 * Inline role editor: a popover trigger that summarises a member's roles,
 * with a switch per available role inside. Reused on the org members page
 * and (potentially) other places where a member has multiple bindable roles.
 */
export function MemberRolesPopover({
  availableRoles,
  assignedRoles,
  memberKey,
  onToggle,
  pending,
  readOnly,
}: MemberRolesPopoverProps) {
  const [open, setOpen] = useState(false);
  const assignedIds = new Set(assignedRoles.map((r) => r.id));
  const summary =
    assignedRoles.length === 0 ? 'No roles' : assignedRoles.map((r) => r.name).join(', ');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="max-w-[9rem] sm:max-w-[12rem]"
          aria-label="Edit member roles"
          data-testid={`member-roles-trigger-${memberKey}`}
          disabled={availableRoles.length === 0}
        >
          <span className="truncate text-xs">{summary}</span>
          <ChevronsUpDown className="icon-sm opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <ul className="divide-y divide-border" data-testid={`member-roles-list-${memberKey}`}>
          {availableRoles.map((role) => {
            const checked = assignedIds.has(role.id);
            const inputId = `member-role-${memberKey}-${role.id}`;
            return (
              <li
                key={role.id}
                className="flex items-start justify-between gap-3 p-3"
                data-testid={`member-role-row-${memberKey}-${role.id}`}
              >
                <label htmlFor={inputId} className="min-w-0 flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{role.name}</span>
                    {role.isBuiltin && (
                      <Badge variant="outline" className="text-[10px]">
                        Built-in
                      </Badge>
                    )}
                  </div>
                  {role.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{role.description}</p>
                  )}
                  {role.slug === 'company-manager' ? (
                    <p className="mt-1 text-xs font-medium text-status-warning">
                      Assigning this role lets this person change sign-in and provisioning for every
                      organization in the company.
                    </p>
                  ) : null}
                </label>
                <Switch
                  id={inputId}
                  checked={checked}
                  onCheckedChange={(next) => onToggle(role.id, next)}
                  disabled={readOnly || pending}
                  aria-label={`Toggle ${role.name} role`}
                />
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
